import { createSlice } from "@reduxjs/toolkit";
import { SelectItem } from "../../../SharedModule/types/shared-module-types";
import {
  getIsoFormatStringWithTimeZone,
  getLastWeekDay,
} from "../../../SharedModule/utils/dateUtils";
import { CostCenterType } from "../../types/times-module-types";
import { isNotNullOrEmpty } from "../../utils/timesUtils";

type TimesQueryState = {
  keepFilters: boolean;
  timeProjectsWithAllLogic: Array<any> | null;
  showAdditionalFilters: boolean;
  isInfoQueryLoaded: boolean;
  costCentersQuery: Array<any> | null;
  costCentersQueryComplete: Array<CostCenterType> | [];
  clientsQuery: Array<any> | null;
  projectsQuery: Array<any> | null;
  projectManagersQuery: Array<any> | null;
  usersQuery: Array<any> | null;
  countriesQuery: Array<any> | null;
  filters: {
    startDate: string;
    endDate: string;
    costCenter: Array<any> | null;
    client: Array<any> | null;
    project: Array<any> | null;
    projectManager: Array<any> | null;
    user: Array<any> | null;
    includeInactiveUsers: boolean;
    taskType: Array<SelectItem> | null;
    country: Array<any> | null;
    hourType: {
      billableHours: boolean;
      nonBillableHours: boolean;
      internalHours: boolean;
      financedHours: boolean;
    };
    nonBillableReasons: Array<SelectItem> | null;
    timeOff: {
      paidTimeOff: boolean;
      unpaidTimeOff: boolean;
    };
    approvalStatus: {
      pendingHours: boolean;
      approvedHours: boolean;
      lockedHours: boolean;
    };
    summary: {
      byUser: boolean;
      byTask: boolean;
      byTaskType: boolean;
      hideEntries: boolean;
    };
  };
};

const today = new Date();

const initialState: TimesQueryState = {
  keepFilters: false,
  timeProjectsWithAllLogic: [],
  showAdditionalFilters: false,
  isInfoQueryLoaded: false,
  costCentersQuery: null,
  costCentersQueryComplete: [],
  clientsQuery: null,
  projectsQuery: null,
  projectManagersQuery: null,
  usersQuery: null,
  countriesQuery: null,
  filters: {
    startDate: getIsoFormatStringWithTimeZone(getLastWeekDay(7, today)),
    endDate: getIsoFormatStringWithTimeZone(getLastWeekDay(1, today)),
    costCenter: null,
    client: null,
    project: null,
    projectManager: null,
    user: null,
    includeInactiveUsers: false,
    taskType: null,
    country: null,
    hourType: {
      billableHours: true,
      nonBillableHours: true,
      internalHours: true,
      financedHours: true,
    },
    nonBillableReasons: null,
    timeOff: {
      paidTimeOff: true,
      unpaidTimeOff: false,
    },
    approvalStatus: {
      pendingHours: true,
      approvedHours: true,
      lockedHours: true,
    },
    summary: {
      byUser: false,
      byTask: false,
      byTaskType: false,
      hideEntries: false,
    },
  },
};

export const timesQuerySlice = createSlice({
  name: "timesQuery",
  initialState,
  reducers: {
    keepFilters: (state, action) => {
      state.keepFilters = action.payload;
    },
    setInfoQuery: (state, action) => {
      state.costCentersQuery = action.payload.costCentersQuery;
      state.clientsQuery = action.payload.clientsQuery;
      state.projectsQuery = action.payload.projectsQuery;
      state.projectManagersQuery = action.payload.projectManagersQuery;
      state.usersQuery = action.payload.usersQuery;
      state.countriesQuery = action.payload.countriesQuery;
      state.isInfoQueryLoaded = true;
    },
    setCostCentersQueryComplete: (state, action) => {
      state.costCentersQueryComplete = action.payload;
    },
    setTimeProjectsWithAllLogicData: (state, action) => {
      state.timeProjectsWithAllLogic = action.payload;
    },
    setAdditionalFilters: (state, action) => {
      state.showAdditionalFilters = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setFilters,
  setInfoQuery,
  setCostCentersQueryComplete,
  setTimeProjectsWithAllLogicData,
  resetFilters,
  keepFilters,
  setAdditionalFilters,
} = timesQuerySlice.actions;

export const IsShowAdditionalFilters = (state: any) => {
  return state.timesQuery.showAdditionalFilters;
};

export const FiltersTouched = (state: any) => {
  // check box filters, should be differents from false. Because is true si sending info to BE (not confuse with default values)
  let filtersTouched: any = {
    costCenter: isNotNullOrEmpty(state.timesQuery.filters.costCenter),
    client: isNotNullOrEmpty(state.timesQuery.filters.client),
    project: isNotNullOrEmpty(state.timesQuery.filters.project),
    projectManager: isNotNullOrEmpty(state.timesQuery.filters.projectManager),
    user: isNotNullOrEmpty(state.timesQuery.filters.user),
    taskType: isNotNullOrEmpty(state.timesQuery.filters.taskType),
    country: isNotNullOrEmpty(state.timesQuery.filters.country),
    hourType:
      state.timesQuery.filters.hourType.billableHours !== true ||
      state.timesQuery.filters.hourType.nonBillableHours !== true ||
      state.timesQuery.filters.hourType.internalHours !== true ||
      state.timesQuery.filters.hourType.financedHours !== true,
    nonBillableReasons: isNotNullOrEmpty(
      state.timesQuery.filters.nonBillableReasons
    ),
    timeOff:
      state.timesQuery.filters.timeOff.paidTimeOff !== true ||
      state.timesQuery.filters.timeOff.unpaidTimeOff !== false,
    approvalStatus:
      state.timesQuery.filters.approvalStatus.pendingHours !== true ||
      state.timesQuery.filters.approvalStatus.approvedHours !== true ||
      state.timesQuery.filters.approvalStatus.lockedHours !== true,
  };
  return filtersTouched;
};

export const SelectFilters = (state: any) => {
  return state.timesQuery.keepFilters
    ? state.timesQuery.filters
    : initialState.filters;
};

export const GetKeepFiltersFlag = (state: any) => {
  return state.timesQuery.keepFilters;
};

export const GetTimeProjectsWithAllLogic = (state: any) => {
  return state.timesQuery.timeProjectsWithAllLogic;
};

export const GetFilters = (state: any) => {
  return state.timesQuery.filters;
};

export const selectIsInfoQueryLoaded = (state: any) => {
  return state.timesQuery.isInfoQueryLoaded;
};

export const SelectCostCentersQueryComplete = (state: any) => {
  return state.timesQuery.costCentersQueryComplete;
};

export const SelectCostCentersQuery = (state: any) => {
  return state.timesQuery.costCentersQuery;
};

export const selectClientsQuery = (state: any) => {
  return state.timesQuery.clientsQuery;
};

export const selectProjectsQuery = (state: any) => {
  return state.timesQuery.projectsQuery;
};

export const selectProjectManagersQuery = (state: any) => {
  return state.timesQuery.projectManagersQuery;
};

export const SelectUsersQuery = (state: any) => {
  return state.timesQuery.usersQuery;
};

export const selectCountriesQuery = (state: any) => {
  return state.timesQuery.countriesQuery;
};

export const SelectInitialStateFilters = () => {
  return initialState.filters;
};

export default timesQuerySlice.reducer;
